/**
 * Chord sequence extraction and shared transposition utilities.
 */
import { isChordLine, parseChordPositions } from './singerFriendly';

const CHROMATIC_SCALE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/**
 * Transpose a single chord symbol by the given number of semitones.
 * E.g. transposeChord('Am', 2) → 'Bm'
 */
export function transposeChord(chord, semitones) {
  const match = chord.match(/^([A-G][#b]?)(.*)/);
  if (!match) return chord;
  const [, root, suffix] = match;
  const flatToSharp = { 'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#' };
  const normalized = flatToSharp[root] || root;
  const idx = CHROMATIC_SCALE.indexOf(normalized);
  if (idx === -1) return chord;
  const newIdx = (idx + semitones + 12) % 12;
  return CHROMATIC_SCALE[newIdx] + suffix;
}

/**
 * Extract an ordered chord sequence from lyrics text, with mapping info
 * for highlighting which chord is at which line position.
 *
 * @param {string} lyricsText - Full lyrics with chord-above-line format
 * @returns {{ chords: string[], mapping: Array<{ lineIndex: number, posInLine: number }> }}
 */
export function extractChordSequenceWithMapping(lyricsText) {
  if (!lyricsText) return { chords: [], mapping: [] };

  const allLines = lyricsText.split('\n');
  const chords = [];
  const mapping = [];

  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i];
    if (isChordLine(line)) {
      const positions = parseChordPositions(line);
      for (let j = 0; j < positions.length; j++) {
        chords.push(positions[j].chord);
        mapping.push({ lineIndex: i, posInLine: j });
      }
    }
  }

  return { chords, mapping };
}

/**
 * Given an activeChordIndex into the global chord sequence,
 * find the corresponding displayLine index in the paired displayLines array.
 * displayLines pairs chord lines with their lyrics lines, so we need to
 * map from raw lineIndex to displayLine index.
 */
export function chordIndexToDisplayLine(mapping, activeIndex, allLines) {
  if (activeIndex < 0 || activeIndex >= mapping.length) return -1;
  const { lineIndex } = mapping[activeIndex];

  // Count how many display lines precede this chord's line.
  // Each chord line + its paired lyrics line = 1 display line.
  // Regular lyrics lines = 1 display line each.
  let displayIdx = 0;
  for (let i = 0; i < allLines.length; i++) {
    if (i === lineIndex) return displayIdx;
    if (isChordLine(allLines[i])) {
      // This chord line + next line = 1 display entry
      const nextLine = i + 1 < allLines.length && !isChordLine(allLines[i + 1]) ? allLines[i + 1] : '';
      if (nextLine) i++; // skip paired line
      displayIdx++;
    } else {
      displayIdx++;
    }
  }
  return -1;
}
